
//AuthContext.jsx//

import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api'; // Add this import

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(() => {
        try {
            const savedUser = localStorage.getItem('user');
            const savedToken = localStorage.getItem('token');
            console.log('🔐 AuthContext: Initial state restoration', {
                hasUser: !!savedUser,
                hasToken: !!savedToken
            });
            if (savedUser && savedToken) {
                const parsed = JSON.parse(savedUser);
                console.log('✅ AuthContext: Restored user from localStorage on init:', parsed.email, parsed.role);
                return parsed;
            }
            return null;
        } catch (err) {
            console.error('❌ AuthContext: Failed to restore initial state:', err);
            // Clear corrupted data
            localStorage.removeItem('user');
            localStorage.removeItem('token');
            return null;
        }
    });
    const [loading, setLoading] = useState(true); // Start with true to check auth on mount
    const [isLoggingOut, setIsLoggingOut] = useState(false); // Track logout state
    const [error, setError] = useState(null);

    // Verify token and restore session on mount
    useEffect(() => {
        const verifySession = async () => {
            const token = localStorage.getItem('token');
            const savedUser = localStorage.getItem('user');

            console.log('🔐 AuthContext: Verifying session...', { hasToken: !!token, hasUser: !!savedUser });

            if (!token || !savedUser) {
                console.log('⚠️ AuthContext: No token or user found in localStorage');
                setLoading(false);
                return;
            }

            // Restore user from localStorage immediately (optimistic)
            let parsedUser = null;
            try {
                parsedUser = JSON.parse(savedUser);
                console.log('✅ AuthContext: Restored user from localStorage:', parsedUser.email, parsedUser.role);
                setUser(parsedUser);
            } catch (err) {
                console.error('❌ AuthContext: Failed to parse saved user:', err);
                setLoading(false);
                return;
            }

            // Then verify token in background - ALWAYS fetch fresh data from server
            try {
                console.log('🔄 AuthContext: Verifying token with server...');
                const data = await api.auth.getCurrentUser({ silent: true });
                console.log('✅ AuthContext: Token verified, user data:', data);
                if (data && data.user) {
                    // CRITICAL: Always update user data from server to ensure role is correct
                    console.log('✅ AuthContext: Updating user data from server');
                    if (parsedUser) {
                        console.log('🔍 AuthContext: Server role:', data.user.role, 'Cached role:', parsedUser.role);
                    }

                    // Force update localStorage and state with server data
                    localStorage.setItem('user', JSON.stringify(data.user));
                    setUser(data.user);

                    // If roles don't match, log warning
                    if (parsedUser && parsedUser.role !== data.user.role) {
                        console.warn('⚠️ AuthContext: Role mismatch detected! Cached:', parsedUser.role, 'Server:', data.user.role);
                        console.warn('⚠️ AuthContext: Forcing update to server role:', data.user.role);
                    }

                    console.log('✅ AuthContext: User role updated to:', data.user.role);
                } else {
                    console.warn('⚠️ AuthContext: No user data in response');
                }
            } catch (err) {
                console.error('❌ AuthContext: Token verification error:', err);
                // Only clear if it's a 401 (unauthorized) error, not network errors
                if (err.message && (err.message.includes('Invalid') || err.message.includes('401') || err.message.includes('Unauthorized') || err.message.includes('token'))) {
                    console.error('❌ AuthContext: Token invalid - clearing session');
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    setUser(null);
                } else {
                    // Network error or other issue - keep the session but log warning
                    console.warn('⚠️ AuthContext: Network error - keeping cached session:', err.message);
                    if (parsedUser) {
                        console.warn('⚠️ AuthContext: Cached role may be outdated:', parsedUser.role);
                    }
                    // Keep the user from localStorage (already set above)
                }
            } finally {
                console.log('✅ AuthContext: Session verification complete');
                setLoading(false);
            }
        };

        verifySession();
    }, []);

    const register = async (userData) => {
        setLoading(true);
        setError(null);

        try {
            const data = await api.auth.register(userData);

            if (data.token && data.user) {
                localStorage.setItem('token', data.token);
                localStorage.setItem('user', JSON.stringify(data.user));
                setUser(data.user);
            }

            return data;
        } catch (error) {
            setError(error.message);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const login = async (credentials) => {
        setLoading(true);
        setError(null);

        try {
            console.log('🔐 AuthContext: Attempting login...');
            // Login without role - backend will automatically detect user's role from credentials
            const data = await api.auth.login(credentials, { silent: true });
            console.log('✅ AuthContext: Login successful', data);

            if (data.token && data.user) {
                console.log('💾 AuthContext: Saving token and user to localStorage');
                console.log('🔍 AuthContext: Login response - Role:', data.user.role, 'Email:', data.user.email);

                // CRITICAL: Always use server response data, never cached data
                localStorage.setItem('token', data.token);
                localStorage.setItem('user', JSON.stringify(data.user));
                setUser(data.user);

                console.log('✅ AuthContext: User state updated:', data.user.email, 'Role:', data.user.role);
                console.log('✅ AuthContext: Stored in localStorage - verifying...');

                // Verify what was stored
                const stored = JSON.parse(localStorage.getItem('user'));
                console.log('✅ AuthContext: Verified stored user role:', stored?.role);

                toast.success('Login successful!');
            } else {
                console.warn('⚠️ AuthContext: Login response missing token or user');
                toast.error('Login failed: Invalid response from server');
            }

            return data;
        } catch (error) {
            console.error('❌ AuthContext: Login failed:', error);
            setError(error.message);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        // Set logging out state to prevent any rendering
        setIsLoggingOut(true);
        // Clear state immediately
        setUser(null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Use replace instead of href to avoid showing dashboard briefly
        // Small delay to ensure state is cleared before redirect
        setTimeout(() => {
            window.location.replace('/login');
        }, 0);
    };

    const updateUser = (userData) => {
        const updatedUser = { ...user, ...userData };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
    };

    // Memoize value to prevent unnecessary re-renders
    const value = useMemo(() => ({
        user,
        loading: loading || isLoggingOut, // Show loading during logout
        error,
        register,
        login,
        logout,
        updateUser,
        isAuthenticated: !!localStorage.getItem('token') && !isLoggingOut
    }), [user, loading, isLoggingOut, error]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};